import { DashboardHeader } from "./DashboardHeader";
import { KpiCard } from "./KpiCard";
import { NavChart } from "./NavChart";
import { PerformanceModal } from "./PerformanceModal";
import { SimpleDonutChart } from "./SimpleDonutChart";
import { useLanguage } from "./LanguageContext";
import type { InvestorPage } from "@/store/slices/navigationSlice";
import { useEffect, useMemo, useState } from "react";
import { db } from "../lib/supabase";
import { useUser } from "@/hooks/useUser";

type DistKey = "dist.long" | "dist.rooms" | "dist.short";
interface DistributionData {
  key: DistKey;
  percentage: number;
  color: string;
}

// Modern fintech icons for KPIs - Premium style with containers and hover states
const ActivoTotalIcon = () => (
  <div className="w-12 h-12 rounded-xl bg-[#F5F6FA] flex items-center justify-center transition-all duration-200 group-hover:bg-[#EDEEF2]">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#444"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-colors duration-200 group-hover:stroke-black"
    >
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </svg>
  </div>
);

const RentasYTDIcon = () => (
  <div className="w-12 h-12 rounded-xl bg-[#F5F6FA] flex items-center justify-center transition-all duration-200 group-hover:bg-[#EDEEF2]">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#444"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-colors duration-200 group-hover:stroke-black"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9,22 9,12 15,12 15,22" />
    </svg>
  </div>
);

const CashOnCashIcon = () => (
  <div className="w-12 h-12 rounded-xl bg-[#F5F6FA] flex items-center justify-center transition-all duration-200 group-hover:bg-[#EDEEF2]">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#444"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-colors duration-200 group-hover:stroke-black"
    >
      <polyline points="22,7 13.5,15.5 8.5,10.5 2,17" />
      <polyline points="16,7 22,7 22,13" />
    </svg>
  </div>
);

const FeesYTDIcon = () => (
  <div className="w-12 h-12 rounded-xl bg-[#F5F6FA] flex items-center justify-center transition-all duration-200 group-hover:bg-[#EDEEF2]">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#444"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-colors duration-200 group-hover:stroke-black"
    >
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 6h8" />
      <circle cx="8" cy="10" r="0.5" />
      <circle cx="12" cy="10" r="0.5" />
      <circle cx="16" cy="10" r="0.5" />
      <circle cx="8" cy="14" r="0.5" />
      <circle cx="12" cy="14" r="0.5" />
      <circle cx="16" cy="14" r="0.5" />
      <circle cx="8" cy="18" r="0.5" />
      <circle cx="12" cy="18" r="0.5" />
      <circle cx="16" cy="18" r="0.5" />
    </svg>
  </div>
);

// (No separate ROE icon — reuse CashOnCashIcon for both widgets)

type UserType = "investor";

interface DashboardProps {
  userType: UserType;
  onPageChange: (page: InvestorPage) => void;
}

// Mock data generation functions are now inside each chart component

export function Dashboard({ userType, onPageChange }: DashboardProps) {
  const { user, profile } = useUser();
  const rawName =
    (profile as any)?.full_name ||
    (user as any)?.full_name ||
    (user?.email
      ? user.email.split("@")[0]
      : userType === "investor"
      ? "Usuario"
      : "Usuario");
  const userName =
    typeof rawName === "string" && rawName.length > 0
      ? rawName.charAt(0).toUpperCase() + rawName.slice(1)
      : "Usuario";
  const { t, getBilingualText } = useLanguage();
  const [distData, setDistData] = useState<DistributionData[] | undefined>(
    undefined
  );
  const [latestNav, setLatestNav] = useState<{
    cash_on_cash?: number | null;
    roi?: number | null;
    period_month?: string | null;
    value?: number | null;
  } | null>(null);
  
  const [totals, setTotals] = useState<{
    valorActual: number;
    rentasAnuales: number;
  }>({ valorActual: 0, rentasAnuales: 0 });
  const [isPerfOpen, setIsPerfOpen] = useState(false);

  // Cargar activos y calcular distribución por tipo
  useEffect(() => {
    const load = async () => {
      try {
        const pageSize = 1000; // suficiente para la cartera típica
        const { data } = await db.getActivosPaged("", 1, pageSize);
        const totalsByType: Record<DistKey, number> = {
          "dist.long": 0,
          "dist.rooms": 0,
          "dist.short": 0,
        };

        const normalizeToKey = (value: any): DistKey => {
          const lower = String(value ?? "")
            .trim()
            .toLowerCase();
          if (!lower || lower === "-" || lower === "n/a") return "dist.short"; // Temporal
          if (lower.includes("tradicional")) return "dist.long"; // Tradicional
          if (lower.includes("larga")) return "dist.long"; // Larga Temporada -> Tradicional
          if (lower.includes("habitacion")) return "dist.rooms"; // Habitaciones
          return "dist.short"; // Temporal
        };

        let sumValorActual = 0;
        let sumRentasAnuales = 0;
        (data ?? []).forEach((row: any) => {
          const key = normalizeToKey(row?.tipo_explotacion);
          totalsByType[key] += 1;
          const va = Number(row?.valor_actual ?? 0) || 0;
          const ra = Number(row?.rentas_anuales ?? 0) || 0;
          sumValorActual += Math.max(0, va);
          sumRentasAnuales += Math.max(0, ra);
        });

        const grandTotal = Object.values(totalsByType).reduce(
          (a, b) => a + b,
          0
        );
        const safeTotal = grandTotal > 0 ? grandTotal : 1; // evitar división por cero

        const computed: DistributionData[] = [
          {
            key: "dist.long",
            percentage: (totalsByType["dist.long"] / safeTotal) * 100,
            color: "#111827",
          },
          {
            key: "dist.rooms",
            percentage: (totalsByType["dist.rooms"] / safeTotal) * 100,
            color: "#6B7280",
          },
          {
            key: "dist.short",
            percentage: (totalsByType["dist.short"] / safeTotal) * 100,
            color: "#D1D5DB",
          },
        ];

        setDistData(computed);
        setTotals({
          valorActual: sumValorActual,
          rentasAnuales: sumRentasAnuales,
        });
      } catch (e) {
        console.error("Error cargando distribución de activos:", e);
        setDistData(undefined);
      }
    };
    load();
  }, []);

  // Load latest NAV month to show KPI widgets (CoC and ROI)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const rows = await db.getNavMonths();
        if (!rows || (Array.isArray(rows) && rows.length === 0)) return;
        const mapped = (rows as any[]).map((r) => ({
          period_month: r.period_start || r.period_month || null,
          cash_on_cash: Number(r.cash_on_cash) || 0,
          roi: Number(r.roi) || 0,
          value: Number(r.value) || 0,
        }));
        // sort desc and take latest
        mapped.sort((a, b) => (b.period_month || "").localeCompare(a.period_month || ""));
        const latest = mapped[0];
        if (mounted) setLatestNav(latest || null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error loading latest NAV month:", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const formatPeriodMonth = (isoDate: string | undefined | null) => {
    if (!isoDate) return "";
    const ym = String(isoDate).slice(0, 7);
    const [yyyy, mm] = ym.split("-");
    const year = Number(yyyy) || NaN;
    const monthIdx = Number(mm) ? Number(mm) - 1 : NaN;
    if (!Number.isFinite(year) || !Number.isFinite(monthIdx) || monthIdx < 0 || monthIdx > 11) return "";
    const month = monthNames[monthIdx] || "";
    return `${month} ${year}`;
  };

  // Mostrar solo el porcentaje actual en gris (no calcular subida/bajada)
  const makeDelta = (currentPct: number | null) => {
    if (currentPct === null || Number.isNaN(currentPct)) {
      return { value: "-", type: "neutral", label: "" } as const;
    }
    return {
      value: `${Number(currentPct).toFixed(1)}%`,
      type: "neutral",
      label: latestNav?.period_month ? formatPeriodMonth(latestNav.period_month) : "",
    } as const;
  };

    

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6">
        {/* Header */}
        <DashboardHeader userName={userName} userType={userType} />

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div
            className="group cursor-pointer order-1 md:order-1"
            onClick={() => onPageChange("portfolio")}
          >
            <KpiCard
              title={getBilingualText("dashboard.kpi.activoTotal")}
              value={new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
              }).format(totals.valorActual)}
              delta={{
                value: "+2.3%",
                type: "positive",
                label: t("time.quarter"),
              }}
              icon={<ActivoTotalIcon />}
            />
          </div>

          <div className="group cursor-pointer min-w-max order-last" onClick={() => window.open("https://docs.google.com/spreadsheets/d/1M0KSUBdVGLBVJa2cvmLQ7OTMtstK4vsE/edit?usp=sharing&ouid=101668140225972699254&rtpof=true&sd=true","_blank","noopener,noreferrer")}>
            <KpiCard
              title={"Cash on Cash"}
              value={
                latestNav && typeof latestNav.cash_on_cash === "number"
                  ? `${Number(latestNav.cash_on_cash).toFixed(1)}%`
                  : "-"
              }
              delta={{
                value: "",
                type: "neutral",
                label: latestNav?.period_month ? formatPeriodMonth(latestNav.period_month) : "",
              }}
              icon={<CashOnCashIcon />}
            />
          </div>

          <div className="group cursor-pointer min-w-max order-last" onClick={() => window.open("https://docs.google.com/spreadsheets/d/1M0KSUBdVGLBVJa2cvmLQ7OTMtstK4vsE/edit?usp=sharing&ouid=101668140225972699254&rtpof=true&sd=true","_blank","noopener,noreferrer") }>
            <KpiCard
              title={"ROE"}
              value={
                latestNav && typeof latestNav.roi === "number"
                  ? `${Number(latestNav.roi).toFixed(1)}%`
                  : "-"
              }
              delta={{
                value: "",
                type: "neutral",
                label: latestNav?.period_month ? formatPeriodMonth(latestNav.period_month) : "",
              }}
              icon={<CashOnCashIcon />}
            />
          </div>

          <div
            className="group cursor-pointer order-2"
            onClick={() => onPageChange("portfolio")}
          >
            <KpiCard
              title={getBilingualText("dashboard.kpi.rentasYTD")}
              value={new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
              }).format(totals.rentasAnuales)}
              delta={{
                value: "+8.1%",
                type: "positive",
                label: t("time.quarter"),
              }}
              icon={<RentasYTDIcon />}
            />
          </div>

          

          {/* <div className="group cursor-pointer" onClick={() => onPageChange("reportes-financiero")}>
            <KpiCard
              title={getBilingualText("dashboard.kpi.cashOnCash")}
              value="15.3%"
              delta={{
                value: "+1.8%",
                type: "positive",
                label: t("time.quarter")
              }}
              icon={<CashOnCashIcon />}
            />
          </div>

          <div className="group cursor-pointer" onClick={() => onPageChange("reportes-financiero")}>
            <KpiCard
              title={getBilingualText("dashboard.kpi.feesYTD")}
              value="€32.500"
              delta={{
                value: "-1.2%",
                type: "negative",
                label: t("time.quarter")
              }}
              icon={<FeesYTDIcon />}
            />
          </div> */}
        </div>

        {/* ChartsRow */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* NAV Evolution Chart */}
          <div className="lg:col-span-2">
            <NavChart onChartClick={() => setIsPerfOpen(true)} />
          </div>

          {/* Distribution Chart */}
          <div className="lg:col-span-1">
            <SimpleDonutChart onPageChange={onPageChange} data={distData} />
          </div>
        </div>

        {/*Recent Activity Table*/}
      </div>

        {/* debug removed */}
        <PerformanceModal open={isPerfOpen} onOpenChange={setIsPerfOpen} />
    </div>
  );
}
